import { z } from "zod";

/**
 * Valeur d'un champ personnalisé (une entrée du sac `customFields`).
 *
 * Primitives uniquement : string | number | boolean | string[] | null.
 * Les dates sont stockées en chaîne ISO 8601 (pas d'objet `Date` ici),
 * les multiselect en tableau de chaînes, `null` = champ explicitement vidé.
 */
export const customFieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

/** Type d'une valeur de champ personnalisé. */
export type CustomFieldValue = z.infer<typeof customFieldValueSchema>;

/**
 * Sac de VALEURS des champs personnalisés d'un document (doctrine 6.7).
 *
 * Record clé -> valeur : chaque clé correspond au `key` d'une
 * `customFieldDefinition` de l'org (cf. `custom-field.ts`, qui porte les
 * DÉFINITIONS ; ici on ne modélise que les valeurs stockées sur le document).
 */
export const customFieldValuesSchema = z.record(
  z.string().min(1),
  customFieldValueSchema,
);

/** Type du sac de valeurs de champs personnalisés. */
export type CustomFieldValues = z.infer<typeof customFieldValuesSchema>;

/**
 * Champ `customFields` optionnel, à étaler dans les schémas d'ENTRÉE des
 * familles (`<X>CreateInputSchema`) via `...customFieldsField`, pour que les
 * apps puissent fournir leurs champs personnalisés à la création.
 */
export const customFieldsField = {
  customFields: customFieldValuesSchema.optional(),
} as const;

/**
 * Champs standard présents sur CHAQUE document Firestore du socle MyTaki.
 *
 * Tout schéma d'entité cœur étend ces champs via `baseEntityFields`.
 * Les dates sont des objets `Date` (les conversions Firestore Timestamp <-> Date
 * sont gérées dans le SDK, pas ici : le data-model décrit le modèle canonique).
 *
 * - `id`            : identifiant du document (id Firestore).
 * - `createdAt`     : date de création.
 * - `updatedAt`     : date de dernière modification.
 * - `createdBy`     : uid de l'auteur de la création.
 * - `updatedBy`     : uid du dernier modificateur.
 * - `archivedAt`    : date d'archivage (soft delete) ou `null` si actif.
 * - `deletedAt`     : date de suppression, ou `null`. SUPPRIMER, c'est archiver
 *                     ET poser cette date : les deux champs ensemble, jamais
 *                     l'un sans l'autre. Un document supprimé n'est donc visible
 *                     NULLE PART côté client (ni dans les listes, ni dans le
 *                     mode Archives) et aucun écran ne permet de le récupérer :
 *                     pour l'utilisateur, supprimé veut dire supprimé. La ligne
 *                     survit en base pour que le support Taki puisse rattraper
 *                     une bêtise ; rien ne la purge encore (c'est le chantier
 *                     `purge-corbeille-30-jours`, qui exige des Cloud
 *                     Functions).
 * - `schemaVersion` : version du schéma du document (pour les migrations).
 * - `customFields`  : sac de champs personnalisés (doctrine 6.7), optionnel.
 */
export const baseEntityFields = {
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: z.string(),
  updatedBy: z.string(),
  archivedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
  schemaVersion: z.number().int().nonnegative(),
  // Sac de champs personnalisés (doctrine 6.7) : fourni par l'APP, jamais
  // rempli par le SDK. Clés = `customFieldDefinitions.key`, valeurs primitives
  // ou string[] (dates en ISO 8601).
  customFields: customFieldValuesSchema.optional(),
} as const;

/**
 * Schéma Zod des seuls champs standard, utile pour valider/typer le socle
 * commun indépendamment d'une entité concrète.
 */
export const BaseEntitySchema = z.object(baseEntityFields);

/** Type des champs standard partagés par toutes les entités. */
export type BaseEntity = z.infer<typeof BaseEntitySchema>;

/**
 * Champs additionnels pour les entités "owner-scoped" (row-level ownership).
 * À étaler dans le schéma d'une entité possédée par un utilisateur.
 *
 * - `ownerId`    : uid du propriétaire de la ligne.
 * - `assignedTo` : uid de l'assigné (peut différer du propriétaire), optionnel.
 */
export const ownerScopedFields = {
  ownerId: z.string(),
  assignedTo: z.string().optional(),
} as const;

/** Schéma Zod des champs owner-scoped. */
export const OwnerScopedSchema = z.object(ownerScopedFields);

/** Type des champs owner-scoped. */
export type OwnerScoped = z.infer<typeof OwnerScopedSchema>;
